import React from 'react';
import './LobbyFactionCarousel.css';
import LobbyFactionArt from './LobbyFactionArt';
import {MENU_SOUND_MANAGER} from '../../../scenes/MenuSoundManager';

// Сколько элементов по каждую сторону от текущего держать в разметке. Крайние
// (±3) прозрачны: они нужны, чтобы входящий в кадр элемент въезжал плавно, а
// не появлялся сразу на своем месте
const CAROUSEL_RENDER_RADIUS = 3;

// Место элемента по расстоянию от текущего: сдвиг — в долях стороны арта,
// масштаб и прозрачность. Соседи заходят под текущий арт и видны краем
const CAROUSEL_SLOTS = [
  {shift: 0, scale: 1, opacity: 1},
  {shift: 0.6, scale: 0.62, opacity: 0.5},
  {shift: 0.98, scale: 0.42, opacity: 0.16},
  {shift: 1.2, scale: 0.3, opacity: 0},
];

/**
 * Расстояние от текущего элемента по кругу: со знаком, кратчайшее — карусель
 * зациклена
 *
 * @param {number} index
 * @param {number} currentIndex
 * @param {number} count
 * @returns {number}
 */
export const getCarouselOffset = (index, currentIndex, count) => {
  const offset = (((index - currentIndex) % count) + count) % count;
  return offset > count / 2 ? offset - count : offset;
};

/**
 * Карусель встроенного выбора фракции десктопного лобби. Своя, а не Embla:
 * в центре крупный квадратный арт текущего элемента (сторона — от
 * доступного места, см. LobbyFactionCarousel.css), соседи ±1 уменьшены и
 * полупрозрачны, ±2 едва видны; смена — плавным сдвигом. Листается стрелками,
 * клавишами ←/→ (когда карусель в фокусе) и нажатием по соседу.
 *
 * Выбранная игроком фракция светится (то же свечение, что в «Все
 * фракции»), а пока карусель стоит на ней, остальные карточки дополнительно
 * затемнены — выбор читается сразу. Стоит пролистнуть — затемнение уходит.
 *
 * Карусель только показывает и листает: что под артом (имя, кнопки), рисует
 * вызывающий через renderControls.
 *
 * @param {Object} props
 * @param {Array<Object>} props.items - элементы (id обязателен)
 * @param {number} props.currentIndex
 * @param {Function} props.onIndexChange - (index) => void
 * @param {Function} props.getItemDisplay - (item) => getLobbyFactionDisplay
 * @param {Function} props.checkIfItemAvailable - (item) => boolean
 * @param {Function} props.getItemMaskLabel - (item) => подпись маски недоступного
 * @param {Object|null} [props.selectFlash] - эффект «выбрано» ({factionId, at})
 * @param {string|null} [props.selectedFactionId] - текущая фракция игрока
 * @param {React.ReactNode} [props.children] - подпись и кнопки под артом
 */
const LobbyFactionCarousel = ({
  items, currentIndex, onIndexChange, getItemDisplay, checkIfItemAvailable, getItemMaskLabel, selectFlash,
  selectedFactionId = null, children,
}) => {
  const count = items.length;
  const isOnSelected = Boolean(selectedFactionId) && items[currentIndex]?.id === selectedFactionId;

  const goTo = (index) => {
    if (count === 0) return;
    MENU_SOUND_MANAGER.playNextCarouselItemClick();
    onIndexChange(((index % count) + count) % count);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      goTo(currentIndex - 1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      goTo(currentIndex + 1);
    }
  };

  const visibleItems = items
    .map((item, index) => ({item, index, offset: getCarouselOffset(index, currentIndex, count)}))
    .filter(({offset}) => Math.abs(offset) <= CAROUSEL_RENDER_RADIUS);

  return (
    <div
      className={`lobby-faction-carousel ${isOnSelected ? 'lobby-faction-carousel_on-selected' : ''}`}
      // Фокус — ради клавиш ←/→: листать с клавиатуры, не целясь в стрелки
      tabIndex={0}
      onKeyDown={handleKeyDown}
      aria-label="Выбор фракции"
    >
      <div className="lobby-faction-carousel_stage">
        <div className="lobby-faction-carousel_track">
          {visibleItems.map(({item, index, offset}) => {
            const slot = CAROUSEL_SLOTS[Math.abs(offset)];
            const display = getItemDisplay(item);
            const isAvailable = checkIfItemAvailable(item);
            const isCurrent = offset === 0;
            const isSelected = item.id === selectedFactionId;
            return (
              <div
                key={item.id}
                className={`lobby-faction-carousel_item ${isCurrent ? 'lobby-faction-carousel_item_current' : ''} ${
                  isSelected ? 'lobby-faction-carousel_item_selected' : ''}`}
                data-faction-id={item.id}
                data-offset={offset}
                style={{
                  transform: `translate(-50%, -50%) translateX(calc(${Math.sign(offset) * slot.shift} * var(--lobby-carousel-art-size))) scale(${slot.scale})`,
                  opacity: slot.opacity,
                  zIndex: CAROUSEL_RENDER_RADIUS + 1 - Math.abs(offset),
                }}
                onClick={isCurrent ? undefined : () => goTo(index)}
                title={isCurrent ? undefined : display.name}
              >
                <LobbyFactionArt
                  display={display}
                  className="lobby-faction-carousel_art"
                  withFrame
                  flashAt={isCurrent && selectFlash?.factionId === item.id ? selectFlash.at : null}
                />
                {!isAvailable && (
                  <div className="lobby-faction-carousel_mask">
                    {isCurrent ? getItemMaskLabel(item) : ''}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <button
          type="button"
          className="lobby-faction-carousel_arrow lobby-faction-carousel_arrow_prev"
          onClick={() => goTo(currentIndex - 1)}
          title="Предыдущая фракция"
        >
          <img src="/img/icons/arrow left.png" alt="Предыдущая"/>
        </button>
        <button
          type="button"
          className="lobby-faction-carousel_arrow lobby-faction-carousel_arrow_next"
          onClick={() => goTo(currentIndex + 1)}
          title="Следующая фракция"
        >
          <img src="/img/icons/arrow right.png" alt="Следующая"/>
        </button>
      </div>
      {children}
    </div>
  );
};

export default LobbyFactionCarousel;
